import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Tooltip, IconButton, Fab } from '@material-ui/core';
import { Add, Refresh, Delete } from '@material-ui/icons';
import { BackButton } from '../../sc-utilities';
import MaterialTable from 'material-table';
import { hasPermission, localization, hasAtLeastOnePermission, getDimensionsOfPagination } from '../../sc-utilities';
import SKUPatternDelete from './SKUPatternDelete';
import * as actions from './store/skuPatternActions';
import * as action from '../../np-core/Auth/store/authActions';
import Toolbar from '../../sc-utilities/Toolbar';
import { MuiTableRows } from '../../../themes';

const styles = (theme) => ({
    '@global': {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: '40px',
            padding: '2px',
            fontSize: '0.7rem',
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];
const tableRef = React.createRef();

const SKUPatternList = (props) => {
    const {
        classes,
        deleting,
        translations,
        permissions,
        onFetchSKUPatterns,
        onFetchSKUPatternsCount,
        fetching,
        saveColumnsFilter,
        activeCustomer,
        onBulkDeleteSKUPatterns,
        tableColumns,
        redirectHandler,
    } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, skuPatternIDs: [] });
    const columnObject = useMemo(
        () => tableColumns,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
    const selectionPermissions = hasAtLeastOnePermission(permissions, [
        'sku_pattern_delete',
        'system_sku_pattern_delete',
    ]);

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: true,
        selection: selectionPermissions,
        selectionProps: (rowData) => {
            let disabled = !selectionPermissions;
            rowData.tableData.disabled = disabled;
            return disabled
                ? {
                      disabled: true,
                      checked: false,
                  }
                : {};
        },
        search: false,
        minBodyHeight: window.innerHeight - 290 < 300 ? 300 : window.innerHeight - 290,
        maxBodyHeight: window.innerHeight - 290,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false,
    });

    const filterQuery = (filters) => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomer;
        query['q'] = q;
        let count = await onFetchSKUPatternsCount(query);
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count);
            query['pageSize'] = dimensions.pageSize;
            rowsPageSizeInit = dimensions.rowsPageSize;
            pageSize = dimensions.pageSize;
        }
        let result = count ? await onFetchSKUPatterns(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const tableTitle = () => {
        return (
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations['sku_patterns'] || 'SKU Patterns'}</Typography>
                {hasPermission(permissions, 'sku_pattern_create') ? (
                    <Fab
                        id="create_sku_pattern"
                        variant="extended"
                        style={{ borderRadius: '1px' }}
                        size="small"
                        color="primary"
                        aria-label="Add"
                        className={classes.fab}
                        onClick={() => redirectHandler(`/settings/sku-pattern/create`)}
                    >
                        <Add size="small" />
                        {translations['create_new'] || 'Create new'}
                    </Fab>
                ) : null}
                <Tooltip title={translations['refresh'] || 'Refresh'}>
                    <IconButton
                        aria-label={translations['refresh'] || 'Refresh'}
                        component="div"
                        disabled={fetching}
                        onClick={() => tableRef.current && tableRef.current.onQueryChange()}
                    >
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        );
    };

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, skuPatternIDs: [] });
    };

    const handleCloseDanger = async () => {
        await onBulkDeleteSKUPatterns({
            SKUPatternIDs: deleteModalState.skuPatternIDs,
            ActiveCustomerID: activeCustomer,
        });
        setDeleteModalState({ open: false, skuPatternIDs: [] });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const handleBulkDelete = (skuPatternIDs) => {
        setDeleteModalState({ ...deleteModalState, open: true, skuPatternIDs: skuPatternIDs });
    };

    const tableActions = [
        {
            tooltip: `${translations['remove_all_selected'] || 'Remove all selected'} ${
                translations['sku_patterns'] || 'SKU Patterns'
            }`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map((skuPattern) => skuPattern.SKUPatternID)),
        },
    ];

    const columnHidden = (newColumnState) => {
        let objToSendColumn = {};
        let objColumns = {};
        columnObject.map((column) => {
            objColumns[`${column.field}`] = column.hidden;
            return objColumns;
        });
        objToSendColumn[`SKUPattern`] = objColumns;
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomer });
        return newColumnState;
    };

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: (props) => (
                        <Toolbar
                            data={props}
                            actions={tableActions}
                            selectedRows={props.selectedRows}
                            translations={translations}
                        />
                    ),
                }}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size;
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {
                    if (tableRef?.current?.tableContainerDiv?.current) {
                        tableRef.current.tableContainerDiv.current.scrollTop = 0;
                    }
                }}
            />
            {hasPermission(permissions, 'sku_pattern_delete') ? (
                <SKUPatternDelete
                    activeCustomer={activeCustomer}
                    entityName={translations['sku_patterns'] || 'SKU Patterns'}
                    translations={translations}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleting={deleting}
                    deleteModalState={deleteModalState}
                />
            ) : null}
        </Paper>
    );
};

const mapStateToProps = (state) => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        translations:
            state.translate && state.translate.translations
                ? state.translate.translations.activeTranslations || {}
                : {},
        permissions: state?.core?.login?.permissions || [],
        deleting: state.setting.skuPatterns.deleting,
        fetching: state.setting.skuPatterns.fetching,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        onFetchSKUPatternsCount: (filter) => dispatch(actions.fetchSKUPatternCount(filter)),
        onFetchSKUPatterns: (filter) => dispatch(actions.fetchSKUPattern(filter)),
        onBulkDeleteSKUPatterns: (skuPattern) => dispatch(actions.bulkDeleteSKUPatterns(skuPattern)),
        saveColumnsFilter: (data) => dispatch(action.saveColumnsFilter(data)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles, { withTheme: true })(SKUPatternList));
